// Tiny user-fetch hook. Calls /api/auth-me on mount, caches the result in
// memory so we don't refetch across route changes within the same tab.
//
// Returns:
//   { user: User, status: 'authenticated' }
//   { user: null, status: 'guest' }
//   { user: null, status: 'loading' }

import { useEffect, useState } from 'react';

export interface DashboardUser {
  id: string;
  email: string;
  name: string | null;
  lead_id: string | null;
}

interface MeResponse {
  user: DashboardUser | null;
}

let cached: DashboardUser | null | undefined = undefined;
let inFlight: Promise<DashboardUser | null> | null = null;

async function fetchMe(): Promise<DashboardUser | null> {
  if (inFlight) return inFlight;
  inFlight = fetch('/api/auth-me', { credentials: 'same-origin' })
    .then(async (r) => (r.ok ? ((await r.json()) as MeResponse).user : null))
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  cached = await inFlight;
  return cached;
}

export function useUser(): {
  user: DashboardUser | null;
  status: 'loading' | 'authenticated' | 'guest';
} {
  const [user, setUser] = useState<DashboardUser | null | undefined>(cached);

  useEffect(() => {
    if (cached !== undefined) return;
    let active = true;
    void fetchMe().then((u) => {
      if (active) setUser(u);
    });
    return () => {
      active = false;
    };
  }, []);

  if (user === undefined) return { user: null, status: 'loading' };
  if (user === null) return { user: null, status: 'guest' };
  return { user, status: 'authenticated' };
}

export function clearUserCache(): void {
  cached = undefined;
  inFlight = null;
}

export async function logout(): Promise<void> {
  await fetch('/api/auth-logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
  clearUserCache();
  window.location.href = '/';
}
